import RowStack from "@common/components/common/row-stack/RowStack";
import { openTeamInboxChatThunk } from "./redux/TeamInboxThunks";
import { makeSelectNameForDisplay } from "./redux/TeamInboxSelectors";
import { selectWhatsappContactById } from "@common/reducers/WhatsAppSelectors";
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import { Box, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';

type ContactDisplayRowProps = {
    contactId: string;
}

function ContactDisplayRow({ contactId }: ContactDisplayRowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Look up the contact from the Redux state using the contactId
    const contact = useAppSelector(state => selectWhatsappContactById(state, contactId));

    // Memoize the selector to prevent creating new selector on every render
    const selectDisplayName = useMemo(
        () => makeSelectNameForDisplay(contactId),
        [contactId]
    );
    const displayName = useAppSelector(selectDisplayName);

    const styles = {
        container: {
            backgroundColor: "transparent",
            "&:hover": {
                backgroundColor: 'var(--wa-row-hover-bg)',
            },
            borderBottom: `1px solid var(--wa-divider)`,
        },
        contactName: {
            fontWeight: 400,
        },
        contactInfo: {
            color: 'var(--wa-secondary-text)',
        },
    };

    const handleContactClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.nativeEvent.stopImmediatePropagation();
        e.stopPropagation();

        // Open via the unified thunk; tempChatData carries the contact until
        // the real chat object is fetched.
        dispatch(openTeamInboxChatThunk({ chatId: contactId, tempChatData: contact }));
    }

    return (
        <RowStack
            key={contactId}
            sx={{
                height: "72px",
                padding: "8px 16px 8px 8px",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                ...styles.container,
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleContactClick(e)}
        >
            <Box position="relative" sx={{ marginInlineEnd: 1.5 }}>
                <ContactAvatar
                    size={49}
                    textSize={16}
                    contactId={contactId}
                />
            </Box>

            <Stack
                sx={{
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    overflow: "hidden",
                    maxWidth: "calc(100% - 65px)"
                }}
            >
                <RowStack sx={{ justifyContent: 'space-between', width: "100%" }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontSize: '17px',
                            color: 'var(--wa-title-text)',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            maxWidth: 'calc(100% - 70px)',
                            flex: 1,
                            ...styles.contactName,
                        }}
                        title={displayName}
                    >
                        {displayName}
                    </Typography>
                </RowStack>

                <RowStack
                    sx={{
                        alignItems: 'center',
                        marginTop: "2px",
                        maxWidth: '100%',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            ...styles.contactInfo,
                        }}
                    >
                        {t('teamInbox.contact')}
                    </Typography>
                </RowStack>
            </Stack>
        </RowStack>
    );
}

export default React.memo(ContactDisplayRow); 